import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import SEOData from '@/components/base/SEOData';
import { faqs, platformStats } from '@/mocks/comics';

const businessFaqs = [
  { question: '平台漫画资源来源是什么？', answer: '我们与多家韩国漫画内容提供商建立了长期合作关系，所有漫画内容均经过正版授权，确保读者能够合法、安全地享受高质量漫画阅读体验。' },
  { question: '如何进行广告合作？', answer: '欢迎品牌方与我们联系广告投放合作。您可以通过页面下方的商务合作表单提交合作意向，我们的商务团队会在3个工作日内与您联系，提供详细的广告方案和报价。' },
  { question: '支持API接入吗？', answer: '目前我们不提供公开API接口。如果您有特殊的内容对接需求，可以通过商务合作表单联系我们，我们将根据具体情况进行评估和沟通。' },
];

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formDataObj = new FormData(form);
    const honeypotVal = (formDataObj.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    setFormStatus('loading');
    setFormError('');
    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name.trim());
      payload.append('email', formData.email.trim());
      payload.append('company', formData.company.trim());
      payload.append('message', formData.message.trim());
      const res = await fetch('http://aetws.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormError(msg.includes('spam') || msg.includes('form data is spam') ? '提交失败，请稍后重试' : msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 品牌介绍 - 商务合作 - 韩漫漫画网"
        description="韩漫漫画网成立于2020年，专注免费韩漫在线阅读，拥有12800+部漫画作品。了解我们的品牌故事、平台数据、商务合作联系方式。"
        keywords="韩漫漫画网,关于我们,品牌介绍,商务合作,免费韩漫"
        canonicalUrl="http://aetws.cn/about"
      />
      <SEOData type="AboutPage" data={{
        name: '关于韩漫漫画网',
        url: 'http://aetws.cn/about',
        description: '韩漫漫画网 - 免费韩漫在线阅读平台品牌介绍',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: '韩漫漫画网', url: 'http://aetws.cn/' },
      }} />
      <SEOData type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://aetws.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://aetws.cn/about' },
        ],
      }} />
      <SEOData type="FAQPage" data={{
        mainEntity: businessFaqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }} />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="max-w-3xl">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight mb-5">
                关于<span className="font-display italic text-accent-500">韩漫漫画网</span>
              </h1>
              <p className="text-foreground-600 text-base leading-relaxed max-w-2xl">
                韩漫漫画网是国内领先的免费韩国漫画在线阅读平台，致力于为漫画爱好者提供高清、流畅、免费的下拉式阅读体验。
                自成立以来，我们始终坚持用户至上的理念，持续引进优质韩漫资源，让每一位读者都能在这里找到属于自己的漫画世界。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
              {[
                { num: platformStats.totalComics.toLocaleString() + '+', label: '漫画作品', icon: 'ri-book-2-line' },
                { num: platformStats.totalChapters.toLocaleString() + '+', label: '漫画章节', icon: 'ri-file-list-3-line' },
                { num: (platformStats.dailyVisitors / 10000).toFixed(0) + '万+', label: '日活跃用户', icon: 'ri-user-line' },
                { num: (platformStats.registeredUsers / 10000).toFixed(0) + '万+', label: '累计用户', icon: 'ri-group-line' },
                { num: platformStats.yearsActive + '年', label: '运营时长', icon: 'ri-time-line' },
                { num: platformStats.updateFrequency, label: '更新频次', icon: 'ri-loop-left-line' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-background-50">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-accent-500/10 flex items-center justify-center">
                    <i className={`${stat.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-1 whitespace-nowrap">{stat.num}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="lg:w-1/2">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20style%20illustration%20of%20reading%20community%2C%20diverse%20group%20of%20people%20enjoying%20comics%20in%20a%20cozy%20modern%20space%2C%20warm%20sunlight%20streaming%20in%2C%20bookshelves%20filled%20with%20comics%2C%20friendly%20atmosphere%2C%20cream%20and%20warm%20wood%20tones%2C%20clean%20composition%2C%20professional%20illustration&width=1200&height=900&seq=about-story-img&orientation=landscape"
                    alt="韩漫漫画网品牌故事"
                    title="韩漫漫画网品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <p className="text-accent-500 text-sm font-medium mb-3">品牌介绍</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩漫漫画网诞生于一群漫画爱好者的共同梦想。我们发现韩国漫画以其独特的画风、精彩的剧情和精致的分镜，在中国拥有大量粉丝，但找到高质量、持续更新的中文资源却并不容易。
                  </p>
                  <p>
                    于是，我们决定打造一个专注<strong>免费韩漫阅读</strong>的平台——<strong>韩漫漫画网</strong>。从最初的几十部漫画，发展到如今超过12,800部作品的规模，我们始终坚持初心：让每一位漫画爱好者都能无障碍地享受<strong>韩国漫画下拉式在线阅读</strong>的乐趣。
                  </p>
                  <p>
                    我们与韩国多家优质内容提供商建立了合作关系，确保平台上的每一部漫画都经过授权，让读者在享受<strong>高清漫画大全</strong>的同时，也为创作者提供应有的支持。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                我们的价值观
              </h2>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                驱动我们不断前进的核心理念
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: '免费阅读', desc: '我们坚信好内容应该被更多人看到。平台所有漫画永久免费，让每一位漫画爱好者都能无负担地沉浸在精彩的韩漫世界中。', icon: 'ri-heart-line' },
                { title: '品质至上', desc: '从漫画选品到页面设计，从加载速度到阅读体验，每一个细节我们都力求完美，只为给读者带来最好的阅读感受。', icon: 'ri-star-line' },
                { title: '持续创新', desc: '我们不断优化技术架构和用户体验，引入最新的阅读技术和功能，让韩漫漫画网始终保持行业领先的水准。', icon: 'ri-flashlight-line' },
              ].map((v, i) => (
                <div key={i} className="rounded-2xl bg-background-50 p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-accent-500/10 flex items-center justify-center">
                    <i className={`${v.icon} text-accent-500 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">关于平台与合作的常见问题</p>
            </div>
            <div className="space-y-3">
              {businessFaqs.map((faq, i) => (
                <details key={i} className="group rounded-xl bg-background-100 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-6 py-5 font-medium text-foreground-950 text-sm list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-300 text-lg"></i>
                  </summary>
                  <div className="px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
                商务合作
              </h2>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                如果您对我们的平台感兴趣，或有广告投放、内容合作等需求，欢迎通过以下表单联系我们。我们会在3个工作日内回复。
              </p>
            </div>
            <form
              onSubmit={handleSubmit}
              className="bg-background-50 rounded-2xl p-8 md:p-10"
            >
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="antispam-field"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名 *</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors"
                    placeholder="您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱 *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors"
                    placeholder="hello@aetws.cn"
                  />
                </div>
              </div>
              <div className="mb-5">
                <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-2">公司名称</label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors"
                  placeholder="您的公司或组织名称"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">合作需求 *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-accent-500 transition-colors resize-none"
                  placeholder="请描述您的合作需求，我们将尽快与您联系"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
              </div>
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full bg-primary-500 text-background-50 py-3.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {formStatus === 'loading' ? '提交中...' : '提交合作需求'}
              </button>
              {formStatus === 'success' && (
                <p className="text-accent-500 text-sm text-center mt-4">感谢您的合作意向！我们会在3个工作日内与您联系。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-500 text-sm text-center mt-4">{formError}</p>
              )}
            </form>
          </div>
        </section>

        {/* Contact Info */}
        <section className="w-full py-14 md:py-16 bg-background-50">
          <div className="w-full max-w-[1000px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'hello@aetws.cn' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '北京市朝阳区望京科技园' },
                { icon: 'ri-customer-service-line', title: '客服热线', content: '400-123-4567（工作日 9:00-18:00）' },
              ].map((info, i) => (
                <div key={i} className="rounded-2xl bg-background-100 p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-500/10 flex items-center justify-center">
                    <i className={`${info.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2">{info.title}</h4>
                  <p className="text-sm text-foreground-500">{info.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}